import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  FaqSearch as UIFoundationFaqSearch,
  FaqSearchProps
} from '@dcxwam/dcx-wam-ui-foundations'
import withComponentRefresh from '../../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import { useSafeSearchParams } from '../../../../utils/use-safe-search-params'
import {
  buildFaqsSearchUrl,
  executeFetchGetCategory,
  executeFetchSearch,
  replaceQuery
} from '../common/FaqsUtils'
import FaqsSearchEditConfig from './FaqsSearchEditConfig'
import { FaqItem, FaqsSearchProps } from './types'

const FaqsSearch: FC<{ model: FaqsSearchProps }> = withComponentRefresh(
  (props) => {
    const [resultsProps, setResultsProps] =
      React.useState<FaqSearchProps['resultsProps']>()
    const [searchParams] = useSafeSearchParams()
    const searchOn = searchParams.get('search')
    const query = searchParams.get('searchText')
    const executeSearch = !!query && searchOn === 'on'
    const { model } = props
    const {
      id,
      isSearch,
      categoryId,
      title,
      inputPlaceholder,
      titleSuccess,
      subTitleSuccess,
      titleNotFound,
      subTitleNotFound,
      faqConfig,
      ...rest
    } = model
    const [titleSearch, setTitleSearch] = React.useState<string>('')

    const editableProps = useExtractEditableProps(props, rest)

    const redirectToSearchPage = useCallback(
      (searchQuery: string) => {
        if (faqConfig?.faqSearchPagePath) {
          window.location.href = buildFaqsSearchUrl(
            faqConfig.faqSearchPagePath,
            searchQuery
          )
        }
      },
      [faqConfig]
    )

    const getCategoryName = useCallback(
      async (catId: string): Promise<void> => {
        const data = await executeFetchGetCategory(catId, faqConfig)
        if (data && Array.isArray(data) && data.length > 0) {
          setTitleSearch(data[0].categoryName ?? '')
        }
      },
      [setTitleSearch]
    )

    const handleSearch = useCallback(
      async (searchQuery: string): Promise<FaqItem[]> => {
        const filteredItems: FaqItem[] = await executeFetchSearch(
          searchQuery,
          faqConfig
        )
        if (filteredItems.length > 0) {
          setResultsProps({
            title: replaceQuery(titleSuccess ?? '', searchQuery),
            subTitle:
              subTitleSuccess?.replace(
                '$articlesLength',
                String(filteredItems.length)
              ) ?? '',
            filteredItems,
            onItemClick: (item: FaqItem) => {
              if (item.url) {
                window.location.href = item.url
              }
            }
          })
        } else {
          setResultsProps({
            title: replaceQuery(titleNotFound ?? '', searchQuery),
            subTitle: subTitleNotFound ?? ''
          })
        }
        return filteredItems
      },
      [faqConfig]
    )

    useEffect(() => {
      if (executeSearch) {
        handleSearch(query)
      }
    }, [executeSearch, query, handleSearch])

    useEffect(() => {
      if (categoryId && !titleSearch) {
        getCategoryName(categoryId)
      }
    }, [titleSearch, categoryId, getCategoryName])

    const faqSearchConfig = useMemo<FaqSearchProps>(
      () => ({
        title: titleSearch || title,
        onSearch: executeSearch ? handleSearch : redirectToSearchPage,
        inputProps: executeSearch
          ? { placeholder: inputPlaceholder, defaultValue: query }
          : { placeholder: inputPlaceholder },
        ...(resultsProps && { resultsProps })
      }),
      [
        titleSearch,
        executeSearch,
        query,
        resultsProps,
        handleSearch,
        redirectToSearchPage
      ]
    )
    return (
      <EditableComponent {...editableProps}>
        {FaqsSearchEditConfig.isEmpty(props?.model) ? (
          FaqsSearchEditConfig.configLabel
        ) : (
          <UIFoundationFaqSearch {...faqSearchConfig} />
        )}
      </EditableComponent>
    )
  }
)

const FaqsSearchWithErrorBoundary = withErrorBoundary(FaqsSearch, {
  componentName: 'FaqsSearch',
  fallback: <ErrorFallback componentName="FaqsSearch" />
})

export default FaqsSearchWithErrorBoundary
